import { useCallback, useEffect, useRef, useState } from 'react'
import { RefCallbackManager } from '../RefCallbackManager'
import anylogger from '@app/anylogger'
import { VirtualRenderWrapper } from './VirtualRenderWrapper'

const log = anylogger('useVisibilityController')

export type EntryRegistration<T extends Object = any> = (ref: Element, obj: T) => void

export type ObservedFunction<T extends Object = any> = (obj: T, isVisible: boolean) => void
interface ObservedRow {
	obj: any
	setVisible: (visible: boolean) => void
}

/**
 * This hook allows you to conditionally render components, only if they are visible in the viewport, with an extra boundary as per the rootMargin of the IntersectionObserver.
 *
 * @param containerRef - This must be the direct ancestor of the conditionally rendered components, and must be scrollable, so the size must be constrained.
 * If it is left to grow to its children's size, the conditional rendering will not work.
 *
 * @param defaultHeight - This is the height that will be reserved for each item that is not rendered because it is not visible.
 * @param options - This is where you can set the IntersectionObserver options, such as the rootMargin. The root is set to the containerRef, so you don't need to set it here.
 * @returns a function that can be used to wrap the components that you want to conditionally render. i.e.
 * @example
 * const createVirtualRenderWrapper = useVirtualRender(containerRef, '50px', { rootMargin: '100px' })
 * return items.map((item, index) => createVirtualRenderWrapper(index, <ItemComponent item={item} />))
 */
export function useVirtualRender(containerRef: HTMLElement | null, defaultHeight: string, options: Omit<IntersectionObserverInit, 'root'>) {
	const [observer, setObserver] = useState<IntersectionObserver>()
	const manager = useRef<RefCallbackManager<ObservedRow>>(new RefCallbackManager())
	const optionsStr = JSON.stringify(options)

	useEffect(() => {
		if (!containerRef) return

		function objObserved(entries: IntersectionObserverEntry[]) {
			if (!manager) return
			for (const entry of entries) {
				manager.current.forOne(entry.target, (intf) => {
					intf.setVisible(entry.isIntersecting)
				})
			}
		}
		const options = { root: containerRef, ...JSON.parse(optionsStr) }

		const obs = new IntersectionObserver(objObserved, options)
		setObserver(obs)
		return () => {
			if (obs) obs.disconnect()
		}
	}, [containerRef, manager, optionsStr])

	const registerRef = useCallback(
		(ref: Element, obj: ObservedRow) => {
			if (!manager || !ref) return
			manager.current.addRef(ref, obj)
			if (ref && observer) observer.observe(ref)
		},
		[manager, observer]
	)
	const createVirtualRenderWrapper = useCallback(
		(index: number, children: any, debugStr?: string) => {
			return (
				<VirtualRenderWrapper
					defaultHeight={defaultHeight}
					registerRef={registerRef}
					key={index}
					debugStr={debugStr ? debugStr : index.toString()}
				>
					{children}
				</VirtualRenderWrapper>
			)
		},
		[defaultHeight, registerRef]
	)
	return createVirtualRenderWrapper
}
